/**
 * Shared helpers that turn backend filter categories into the front-end Filters
 * shape used by FilterPanel, UtilityToolbar, and the saved-search execution path.
 *
 * Range filters (`kind === 'range'`) carry extra runtime fields (`minValue`,
 * `maxValue`, `step`) that are not on the typed `MkFilter` surface, so we read
 * them through an internal cast.
 */
import type { MkFilterCategory } from '../services/searchTypes'
import type { FilterControlState, Filters, FilterSection } from '../components/FilterPanel/FilterPanel'

type RangeFilterExtras = {
  minValue?: number
  maxValue?: number
  step?: number
}

type FilterControlEntry = Filters['controls'][string][string]

export const DEFAULT_FILTER_SORT: Filters['sort'] = {
  active: 'recommended',
  options: [
    { label: 'Recommended', value: 'recommended' },
    { label: 'Alphabetical (A-Z)', value: 'A-Z' },
    { label: 'Alphabetical (Z-A)', value: 'Z-A' },
  ],
}

const emptyFilters = (): Filters => ({
  controls: {},
  sections: [],
  sort: DEFAULT_FILTER_SORT,
  state: {},
})

/** Range filters carry `minValue/maxValue/step` on the wire; non-range filters don't. */
const isRange = (category: MkFilterCategory): boolean => category.kind === 'range'

const buildControl = (filter: MkFilterCategory['filters'][number], category: MkFilterCategory): FilterControlEntry => {
  const range = filter as MkFilterCategory['filters'][number] & RangeFilterExtras
  const control = {
    hidden: false,
    label: filter.label,
    type: category.kind || 'checkbox',
    icon: filter.icon,
  } as FilterControlEntry
  if (isRange(category)) {
    Object.assign(control, { minValue: range.minValue, maxValue: range.maxValue, step: range.step })
  }
  return control
}

// MkFilter.state is `string | boolean` (the API still emits legacy 'on'/'off' for some checkboxes/switches).
// Non-range controls need a real boolean here — passing the raw string downstream makes `checked={state.value}`
// in ZinCheckbox/ZinSwitch render as checked for *any* non-empty string (including 'off'), and the outbound
// filter builder drops non-boolean/non-array values entirely. Range kinds may carry a serialized range string
// (e.g. "500,1000"); preserve those so the slider can round-trip.
const coerceStateValue = (state: MkFilterCategory['filters'][number]['state'], isRangeKind: boolean): FilterControlState['value'] => {
  if (isRangeKind) return state || false
  return state === true || state === 'on'
}

const buildControlState = (filter: MkFilterCategory['filters'][number], category: MkFilterCategory): FilterControlState => {
  const range = filter as MkFilterCategory['filters'][number] & RangeFilterExtras
  const base: FilterControlState = {
    count: filter.count || 0,
    value: coerceStateValue(filter.state, isRange(category)),
  }
  if (isRange(category) && range.minValue !== undefined && range.maxValue !== undefined) {
    return { ...base, currentMinValue: range.minValue, currentMaxValue: range.maxValue }
  }
  return base
}

/**
 * Apply backend categories to a Filters object in-place. When `overwrite` is
 * false, existing sections/controls/state entries are preserved and only
 * missing keys are added (used by saved-search broadening).
 */
const applyCategories = (target: Filters, categories: MkFilterCategory[], overwrite: boolean): void => {
  categories.forEach((category) => {
    const catKey = category.category
    if (!catKey) return
    const sectionMissing = !target.state[catKey]
    if (sectionMissing) {
      target.sections.push({ name: category.label || catKey, key: catKey })
      target.state[catKey] = {}
      target.controls[catKey] = target.controls[catKey] || {}
    }
    if (!Array.isArray(category.filters)) return
    category.filters.forEach((filter) => {
      if (!filter?.key) return
      const filterMissing = !target.state[catKey][filter.key]
      if (overwrite || filterMissing) {
        target.controls[catKey][filter.key] = buildControl(filter, category)
        target.state[catKey][filter.key] = buildControlState(filter, category)
      }
    })
  })
}

/**
 * Build a fresh Filters object from backend categories. Used when the response
 * is authoritative (initial filter fetch, full search response).
 */
export function buildFiltersFromCategories(categories: MkFilterCategory[]): Filters {
  const filters = emptyFilters()
  applyCategories(filters, categories, true)
  return filters
}

/**
 * Merge backend categories into an existing Filters object. Existing entries
 * are preserved; only missing sections, controls, and state keys are added.
 * Returns a new object; the input is not mutated.
 */
export function broadenFiltersWithCategories(existing: Filters, categories: MkFilterCategory[]): Filters {
  const next: Filters = {
    ...existing,
    sections: [...existing.sections],
    controls: { ...existing.controls },
    state: { ...existing.state },
  }
  Object.keys(next.controls).forEach((k) => {
    next.controls[k] = { ...next.controls[k] }
  })
  Object.keys(next.state).forEach((k) => {
    next.state[k] = { ...next.state[k] }
  })
  applyCategories(next, categories, false)
  return next
}

/**
 * Flip `value = true` for every saved key wherever it lives, across all
 * sections. Useful when the saved payload's category bucket is unreliable
 * (e.g. the SavedSearches label maps haven't loaded yet).
 * Returns a new object; the input is not mutated.
 */
export function markFiltersSelectedByKey(filters: Filters, savedKeys: Iterable<string>): Filters {
  const savedKeySet = savedKeys instanceof Set ? (savedKeys as Set<string>) : new Set<string>(savedKeys)
  if (savedKeySet.size === 0) return filters
  const next: Filters = { ...filters, state: { ...filters.state } }
  Object.keys(next.state).forEach((sectionKey) => {
    const section = next.state[sectionKey]
    if (!section) return
    let cloned: FilterSection | undefined
    Object.keys(section).forEach((filterKey) => {
      if (!savedKeySet.has(filterKey)) return
      const ctrl = section[filterKey]
      if (!ctrl || typeof ctrl.value !== 'boolean') return
      cloned = cloned || { ...section }
      cloned[filterKey] = { ...ctrl, value: true }
    })
    if (cloned) next.state[sectionKey] = cloned
  })
  return next
}
